// HotelContactsTable.jsx
// Main table: shows hotel contacts and their email-type subscriptions

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Axios from "axios";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import { GBH_ID, SanitizeHotelID, URLAPI } from "../configuration";
import HoverPaper from "./shared/HoverPaper";
import Icons from "../helpers/icons";
import Colours from "../helpers/colours";
import TableHeadSort from "./TableHeadSort";
import ExternalRecipientsDrawer from "./ExternalRecipientsDrawer";

const HotelExternalsTable = ({ hotelID, auth }) => {
    const [extRows, setExtRows] = useState([]);
    const [extReportTypes, setExtReportTypes] = useState([]);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("Name");
    const [modalOpen, setModalOpen] = useState(false);
    const [extPending, setExtPending] = useState({}); // { "RecipientID-ReportType": boolean }
    const [extSaving, setExtSaving] = useState(false);

    const headerStyles = {
        fontSize: "0.9em",
        backgroundColor: Colours.pillarBackgroundAttract,
        textAlign: "left",
        border: "1px solid lightgrey",
    };

    const tableHeaders = useMemo(() => {
        return [
            { id: "Name", label: "Name", styles: headerStyles },
            {
                id: "Email",
                label: "Contact email",
                ignore: true,
                styles: headerStyles,
            },
            ...(extReportTypes ?? []).map((et) => ({
                id: et,
                label: et === "LastMonthSummaryTable" ? "Monthly emails" : et, // TODO: review this when we handle more than just monthly emails
                ignore: true,
                styles: { ...headerStyles, textAlign: "center" },
            })),
        ];
    }, [extReportTypes]);

    const fetchExternal = useCallback(async () => {
        console.log({ auth });
        if (!hotelID) return;
        try {
            const hid = SanitizeHotelID(hotelID, GBH_ID);

            const res = await Axios.get(
                `${URLAPI}/hotels/externals/assignments`,
                {
                    params: { hotel_id: hid, only_enabled: false },
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${auth.user.jwt}`,
                    },
                },
            );

            const rows = res?.data?.data ?? [];

            // comment: collect dynamic report types
            const types = Array.from(
                new Set(rows.map((r) => r.ReportType)),
            ).sort((a, b) => String(a).localeCompare(String(b)));
            setExtReportTypes(types);

            // comment: build recipient rows with an 'assign' map (reportType -> bool)
            const byRid = new Map();
            rows.forEach((r) => {
                if (!byRid.has(r.RecipientID)) {
                    byRid.set(r.RecipientID, {
                        RecipientID: r.RecipientID,
                        Email: r.Email,
                        Name:
                            r.FirstName || r.LastName
                                ? `${r.FirstName ?? ""} ${
                                      r.LastName ?? ""
                                  }`.trim()
                                : null,
                        RecipientEnabled: !!r.RecipientEnabled,
                        assign: {}, // comment: here we'll store reportType -> enabled
                    });
                }
                const row = byRid.get(r.RecipientID);
                row.assign[r.ReportType] = !!r.AssignmentEnabled;
            });

            setExtRows(
                Array.from(byRid.values()).sort((a, b) =>
                    String(a.Email || "").localeCompare(String(b.Email || "")),
                ),
            );
        } catch (err) {
            // setError(err.message);
        }
    }, [hotelID]);

    // Trigger fetch on mount / hotel change
    useEffect(() => {
        fetchExternal();
    }, [fetchExternal]);

    const sortedContacts = useMemo(() => {
        // if (orderBy !== "Name") return contacts;
        const sorted = [...extRows];
        sorted.sort((a, b) => {
            const nameA = a.Name?.toLowerCase() ?? "";
            const nameB = b.Name?.toLowerCase() ?? "";
            return order === "asc"
                ? nameA.localeCompare(nameB)
                : nameB.localeCompare(nameA);
        });
        return sorted;
    }, [extRows, order, orderBy]);

    useEffect(() => {
        console.log({ extRows, extReportTypes });
    }, [extRows, extReportTypes]);

    const extIsChecked = (recipientID, reportType, row) => {
        const key = `${recipientID}-${reportType}`;
        if (key in extPending) return !!extPending[key];
        return !!row?.assign?.[reportType];
    };

    // comment: toggle pending value
    const extToggle = (recipientID, reportType, checked) => {
        const key = `${recipientID}-${reportType}`;
        setExtPending((prev) => ({ ...prev, [key]: checked }));
    };

    // comment: detect if there are actual changes vs original row.assign
    const extHasChanges = useMemo(() => {
        // build a quick lookup: "rid-rt" -> original bool
        const original = new Map();
        extRows.forEach((r) => {
            extReportTypes.forEach((rt) => {
                original.set(`${r.RecipientID}-${rt}`, !!r.assign?.[rt]);
            });
        });
        // any pending entry whose value differs from original?
        return Object.entries(extPending).some(
            ([k, v]) => original.get(k) !== !!v,
        );
    }, [extPending, extRows, extReportTypes]);

    // comment: save all pending changes via POST upsert to your endpoint
    const handleSaveExternal = async () => {
        if (!extHasChanges) return;
        setExtSaving(true);
        const hid = SanitizeHotelID(hotelID, GBH_ID);

        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.user.jwt}`,
        };

        // comment: POST body: { recipient_id, hotel_id, report_type, enabled }
        const ops = Object.entries(extPending).map(([key, val]) => {
            const [rid, rt] = key.split("-");
            return Axios.post(
                `${URLAPI}/hotels/externals/assignments`,
                {
                    recipient_id: parseInt(rid, 10),
                    hotel_id: hid,
                    report_type: rt,
                    enabled: val ? "True" : "False",
                },
                { headers },
            );
        });

        try {
            await Promise.all(ops);
            // comment: refresh from server so extRows/extReportTypes reflejan el estado real
            await fetchExternal();
            setExtPending({});
        } catch (err) {
            // setError(err.message);
            console.error(err);
        } finally {
            setExtSaving(false);
        }
    };

    return (
        <>
            {/* ---- main card ---- */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingTop: 2,
                    // paddingBottom: 2,
                    mt: 4,
                }}
            >
                <Typography sx={{}} fontWeight={"bold"} variant="h6">
                    EXTERNAL CONTACTS
                </Typography>

                <Button
                    variant="text"
                    onClick={() => setModalOpen(true)}
                    startIcon={<Icons.Edit />}
                >
                    Edit external contact list
                </Button>
                <ExternalRecipientsDrawer
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    hotelID={hotelID}
                    auth={auth}
                    onSaved={() => {
                        // setModalOpen(false);
                        fetchExternal(); // refresca tu tabla después de cambios
                    }}
                />
            </Box>
            <Box component={HoverPaper}>
                {extRows.length === 0 ? (
                    <Typography sx={{ p: 2 }}>
                        No external contacts found for this hotel.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHeadSort
                            stickyHeads={0}
                            headers={tableHeaders}
                            order={order}
                            orderBy={orderBy}
                            setOrder={setOrder}
                            setOrderBy={setOrderBy}
                        />
                        <TableBody>
                            {sortedContacts.map((c) => (
                                <TableRow key={c.AccountID}>
                                    <TableCell
                                        style={{
                                            border: "1px solid lightgrey",
                                        }}
                                    >
                                        {c.Name ?? "—"}
                                    </TableCell>
                                    <TableCell
                                        style={{
                                            border: "1px solid lightgrey",
                                        }}
                                    >
                                        {c.Email}
                                    </TableCell>

                                    {(extReportTypes ?? []).map((et) => (
                                        <TableCell
                                            style={{
                                                border: "1px solid lightgrey",
                                            }}
                                            key={et}
                                            align="center"
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={extIsChecked(
                                                    c.RecipientID,
                                                    et,
                                                    c,
                                                )} // comment: uses pending override
                                                disabled={!c.RecipientEnabled} // comment: lock if recipient disabled
                                                onChange={(e) =>
                                                    extToggle(
                                                        c.RecipientID,
                                                        et,
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
                <Button
                    variant="contained"
                    disabled={!extHasChanges || extSaving}
                    onClick={handleSaveExternal}
                >
                    {extSaving ? "Saving…" : "Save"}
                </Button>
            </Box>
        </>
    );
};

export default HotelExternalsTable;
